/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: CallbackProfileFormView.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { ChangeEvent, useCallback, useState, useMemo, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { PhoneIncoming, Search, Copy, Plus } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { ModuleFormHeader } from '../../components/layouts';
import { FixedActionBar } from '../../components/ui/FixedActionBar';
import { useSaveWithAction } from '../../hooks/useSaveWithAction';
import { QuickSearchModal } from '../../components/ui/QuickSearchModal';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import Toggle from '../../components/ui/Toggle';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import RecordingSelect from '../../components/ui/RecordingSelect';
import { CallbackStrategy } from './types';
import { STRATEGY_OPTIONS } from './constants';
import { navigateToCallbackProfileDuplicate } from './callbackProfilesActions';
import { useCallbackProfileData, useCallbackProfileForm } from './hooks';
import { fetchQuickListCallbackProfiles } from './api';

export default function CallbackProfileFormView() {
  const { t } = useI18n();
  const navigate = useNavigate();
  const { id } = useParams<{ id?: string }>();
  const [searchParams] = useSearchParams();
  const duplicateId = searchParams.get('duplicate');
  const isEditMode = Boolean(id && id !== 'new');

  const { mappedData, isLoading: isDataLoading } = useCallbackProfileData(id, duplicateId);

  const {
    formState,
    extraSettings,
    errors,
    isSaving,
    hasChanges,
    handleFieldChange,
    handleExtraSettingsChange,
    handleSave,
    handleCancel,
  } = useCallbackProfileForm(id, duplicateId, mappedData);

  // Enhanced save handler for useSaveWithAction
  const handleSaveInternal = useCallback(async () => {
    const savedData = await handleSave();

    // If no changes were detected, return current data to allow navigation
    if (!savedData && isEditMode && id) {
      // Return current data with ID/UUID for navigation purposes
      return { id: Number(id), uuid: Number(id) };
    }

    return savedData;
  }, [handleSave, isEditMode, id]);

  // Use reusable hook for save actions
  const handleSaveWithAction = useSaveWithAction({
    onSave: handleSaveInternal,
    listPath: '/pbx/call-center/callback-profiles',
    newPath: '/pbx/call-center/callback-profiles/new',
    mode: isEditMode ? 'edit' : 'create',
    getEditPath: (savedData) => {
      // Callback Profiles use ID for navigation
      return `/pbx/call-center/callback-profiles/${savedData.uuid || savedData.id}`;
    },
    getFormData: () => ({
      formState,
      extraSettings
    }),
    storageKey: 'callbackProfiles:saveAndNewData'
  });

  const [showQuickList, setShowQuickList] = useState(false);
  const [quickListItems, setQuickListItems] = useState<Array<{ id: number; name: string; uuid?: string }>>([]);
  const [quickListSearch, setQuickListSearch] = useState('');
  const [isQuickListLoading, setIsQuickListLoading] = useState(false);

  const loadQuickList = useCallback(async () => {
    setIsQuickListLoading(true);
    try {
      const result = await fetchQuickListCallbackProfiles(quickListSearch);
      setQuickListItems(result);
    } catch {
      // Error handled by API layer, just set empty
      setQuickListItems([]);
    } finally {
      setIsQuickListLoading(false);
    }
  }, [quickListSearch]);

  useEffect(() => {
    if (showQuickList) {
      void loadQuickList();
    }
  }, [showQuickList, loadQuickList]);

  const filteredQuickList = useMemo(() => {
    const query = quickListSearch.trim().toLowerCase();
    if (!query || query.length < 2) {
      return quickListItems;
    }

    return quickListItems.filter((item) =>
      item.name.toLowerCase().includes(query)
    );
  }, [quickListItems, quickListSearch]);


  const handleStrategyChange = useCallback((event: ChangeEvent<HTMLSelectElement>) => {
    const { value } = event.target;
    if (STRATEGY_OPTIONS.includes(value as CallbackStrategy)) {
      handleFieldChange('strategy', value as CallbackStrategy);
    }
  }, [handleFieldChange]);

  const handleDuplicateFromForm = useCallback(() => {
    if (!isEditMode || !id) return;
    navigateToCallbackProfileDuplicate(navigate, id);
  }, [id, isEditMode, navigate]);

  return (
    <div className="p-6 pb-12 space-y-4">
      <ModuleFormHeader
        icon={<PhoneIncoming className="h-5 w-5 text-primary" />}
        title={isEditMode ? `${t('callbackProfiles.form.editTitle')} - ${formState.name || ''}` : t('callbackProfiles.form.createTitle')}
        backButtonText={t('common.list')}
        onBack={() => navigate(-1)}
        actions={[
          {
            icon: <Search className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('callbackProfiles.quickList.tooltip', 'Search'),
            onClick: () => {
              setShowQuickList(true);
              void loadQuickList();
            },
          },
          ...(isEditMode
            ? [{
              icon: <Copy className="h-5 w-5 text-muted-foreground" />,
              tooltip: t('common.duplicate'),
              onClick: handleDuplicateFromForm,
            }]
            : []),
          {
            icon: <Plus className="h-5 w-5 text-muted-foreground" />,
            tooltip: t('callbackProfiles.actions.add'),
            onClick: () => navigate('/pbx/call-center/callback-profiles/new'),
          },
        ]}
      />

      <div className="space-y-4 pb-10">
        <FormBox title={t('callbackProfiles.form.sections.general')} icon={<PhoneIncoming className="h-5 w-5" />}>
          <div className="grid grid-cols-12 gap-3">
            {/* L�nea 1: Profile Name | Strategy */}
            {/* Profile Name */}
            <SimpleTooltip content={t('callbackProfiles.form.fields.name.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
              <Label htmlFor="name" className="text-left">
                {t('callbackProfiles.form.fields.name.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                id="name"
                value={formState.name}
                onChange={(event) => handleFieldChange('name', event.target.value)}
                placeholder={t('callbackProfiles.form.fields.name.placeholder')}
                required
                error={errors.name}
              />
            </div>

            {/* Strategy */}
            <SimpleTooltip content={t('callbackProfiles.form.fields.strategy.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
              <Label htmlFor="strategy" className="text-left">
                {t('callbackProfiles.form.fields.strategy.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <select
                id="strategy"
                value={formState.strategy}
                onChange={handleStrategyChange}
                className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
              >
                {STRATEGY_OPTIONS.map((strategy) => (
                  <option key={strategy} value={strategy}>
                    {t(`callbackProfiles.strategies.${strategy}`)}
                  </option>
                ))}
              </select>
            </div>

            {/* Description � spans full width */}
            <SimpleTooltip content={t('callbackProfiles.form.fields.description.tooltip')} className="col-span-12 md:col-span-3 lg:col-span-3">
              <Label htmlFor="description" className="text-left">
                {t('callbackProfiles.form.fields.description.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-12 md:col-span-9 lg:col-span-9">
              <Input
                id="description"
                value={formState.description}
                onChange={(event) => handleFieldChange('description', event.target.value)}
                placeholder={t('callbackProfiles.form.fields.description.placeholder')}
              />
            </div>

            {/* L�nea 2: Confirm Sound | Max Attempts */}
            {/* Confirm Sound */}
            <SimpleTooltip content={t('callbackProfiles.form.fields.confirmSound.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
              <Label htmlFor="confirmSound" className="text-left">
                {t('callbackProfiles.form.fields.confirmSound.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <RecordingSelect
                id="confirmSound"
                value={formState.confirmSound}
                onChange={(value) => handleFieldChange('confirmSound', value)}
                placeholder={t('callbackProfiles.form.fields.confirmSound.placeholder')}
                returnType="id"
                showPlayButton
              />
            </div>

            {/* Max Attempts */}
            <SimpleTooltip content={t('callbackProfiles.form.fields.maxAttempts.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
              <Label htmlFor="maxAttempts" className="text-left">
                {t('callbackProfiles.form.fields.maxAttempts.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                id="maxAttempts"
                type="number"
                min="1"
                max="10"
                value={formState.maxAttempts}
                onChange={(event) => handleFieldChange('maxAttempts', event.target.value)}
                placeholder={t('callbackProfiles.form.fields.maxAttempts.placeholder')}
                error={errors.maxAttempts}
              />
            </div>

            {/* L�nea 3: Thanks Sound | Timeout (s) */}
            {/* Thanks Sound */}
            <SimpleTooltip content={t('callbackProfiles.form.fields.thanksSound.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
              <Label htmlFor="thanksSound" className="text-left">
                {t('callbackProfiles.form.fields.thanksSound.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <RecordingSelect
                id="thanksSound"
                value={formState.thanksSound}
                onChange={(value) => handleFieldChange('thanksSound', value)}
                placeholder={t('callbackProfiles.form.fields.thanksSound.placeholder')}
                returnType="id"
                showPlayButton
              />
            </div>

            {/* Timeout (seconds) */}
            <SimpleTooltip content={t('callbackProfiles.form.fields.timeoutSeconds.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
              <Label htmlFor="timeoutSeconds" className="text-left">
                {t('callbackProfiles.form.fields.timeoutSeconds.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                id="timeoutSeconds"
                type="number"
                min="1"
                value={formState.timeoutSeconds}
                onChange={(event) => handleFieldChange('timeoutSeconds', event.target.value)}
                placeholder={t('callbackProfiles.form.fields.timeoutSeconds.placeholder')}
                error={errors.timeoutSeconds}
              />
            </div>

            {/* L�nea 4: Announcement Sound | Retry Interval */}
            {/* Announcement Sound */}
            <SimpleTooltip content={t('callbackProfiles.form.fields.announcementSound.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
              <Label htmlFor="announcementSound" className="text-left">
                {t('callbackProfiles.form.fields.announcementSound.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <RecordingSelect
                id="announcementSound"
                value={formState.announcementSound}
                onChange={(value) => handleFieldChange('announcementSound', value)}
                placeholder={t('callbackProfiles.form.fields.announcementSound.placeholder')}
                returnType="id"
                showPlayButton
              />
            </div>

            {/* Retry Interval (seconds) */}
            <SimpleTooltip content={t('callbackProfiles.form.fields.retryInterval.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
              <Label htmlFor="retryInterval" className="text-left">
                {t('callbackProfiles.form.fields.retryInterval.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                id="retryInterval"
                type="number"
                min="1"
                value={formState.retryInterval}
                onChange={(event) => handleFieldChange('retryInterval', event.target.value)}
                placeholder={t('callbackProfiles.form.fields.retryInterval.placeholder')}
                error={errors.retryInterval}
              />
            </div>

            {/* L�nea 5: DTMF Key | Enabled */}
            {/* DTMF Key */}
            <SimpleTooltip content={t('callbackProfiles.form.fields.callbackKey.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
              <Label htmlFor="callbackKey" className="text-left">
                {t('callbackProfiles.form.fields.callbackKey.label')}
              </Label>
            </SimpleTooltip>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Input
                id="callbackKey"
                value={formState.callbackKey}
                onChange={(event) => handleFieldChange('callbackKey', event.target.value)}
                placeholder={t('callbackProfiles.form.fields.callbackKey.placeholder')}
                maxLength={1}
              />
            </div>

            {/* Enabled */}
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <SimpleTooltip content={t('callbackProfiles.form.fields.enabled.tooltip')}>
                <Label htmlFor="enabled" className="text-left">
                  {t('callbackProfiles.form.fields.enabled.label')}
                </Label>
              </SimpleTooltip>
            </div>
            <div className="col-span-12 md:col-span-6 lg:col-span-3">
              <Toggle
                id="enabled"
                value={formState.enabled}
                onChange={(value) => handleFieldChange('enabled', value)}
              />
            </div>
          </div>
        </FormBox>
      </div>

      <FixedActionBar
        onSaveWithAction={handleSaveWithAction}
        onCancel={handleCancel}
        isSaving={isSaving}
        isLoading={isDataLoading}
        isEditMode={isEditMode}
        saveText={t('common.save')}
        cancelText={t('common.cancel')}
        useSaveDropdown={true}
        saveDropdownStorageKey="callbackProfiles:saveActionPreference"
      />

      <QuickSearchModal
        isOpen={showQuickList}
        onClose={() => {
          setShowQuickList(false);
          setQuickListSearch('');
          setIsQuickListLoading(false);
        }}
        title={t('callbackProfiles.quickList.title', 'Callback Profiles')}
        items={filteredQuickList}
        isLoadingInitial={isQuickListLoading}
        isSearching={false}
        searchValue={quickListSearch}
        onSearchChange={setQuickListSearch}
        onItemSelect={(item) => {
          setShowQuickList(false);
          setQuickListSearch('');
          navigate(`/pbx/call-center/callback-profiles/${item.uuid || item.id}`, { replace: true });
        }}
        currentItemId={id}
        renderItem={(item, isCurrent) => (
          <button
            type="button"
            disabled={isCurrent}
            onClick={() => {
              if (isCurrent) return;
              setShowQuickList(false);
              setQuickListSearch('');
              navigate(`/pbx/call-center/callback-profiles/${item.uuid || item.id}`, { replace: true });
            }}
            className={`w-full text-left p-3 rounded-md border border-border transition-colors ${isCurrent
              ? 'bg-primary/20 border-2 border-primary cursor-default opacity-60'
              : 'hover:bg-muted/40'
              }`}
          >
            <div className="font-medium text-foreground">
              {item.name}
            </div>
          </button>
        )}
        searchPlaceholder={t('callbackProfiles.quickList.searchPlaceholder', 'Search by name')}
        emptyMessage={t('callbackProfiles.quickList.empty', 'No callback profiles found')}
        headerIcon={<PhoneIncoming className="h-5 w-5" />}
        minSearchChars={2}
      />
    </div>
  );
}
